'use client';

import React, { useEffect, useMemo, useState } from 'react';
import LandingFooter from '@/app/components/LandingFooter';
import PublicHero from '@/app/components/PublicHero';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle, AlertTriangle, MessageSquare, Phone, Mail, Globe, MessageCircle } from 'lucide-react';
import DOMPurify from 'isomorphic-dompurify';
import { useTranslation } from '@/i18n';

type ContactStatus = 'new' | 'in_progress' | 'replied' | 'closed' | 'spam';

export default function ContactPage() {
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pageTitle, setPageTitle] = useState(t('publicWebsite:contact.heroTitle') || (locale === 'ar' ? 'تواصل معنا' : 'Contact'));
  const [pageBody, setPageBody] = useState('');

  const [platformName, setPlatformName] = useState('ProFlow');
  const [companyName, setCompanyName] = useState('ProFlow');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [address, setAddress] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitWarning, setSubmitWarning] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const supabase = createClient();
        const [pageRes, settingsRes] = await Promise.all([
          supabase
            .from('public_pages')
            .select('title, body')
            .eq('slug', 'contact')
            .eq('status', 'published')
            .limit(1)
            .maybeSingle(),
          supabase
            .from('platform_public_settings')
            .select('platform_name, company_name, support_email, support_phone, company_address, website_url, whatsapp_number')
            .limit(1)
            .maybeSingle(),
        ]);

        if (pageRes.error) throw pageRes.error;
        if (settingsRes.error) throw settingsRes.error;

        setPageTitle(String(pageRes.data?.title || (t('publicWebsite:contact.heroTitle') || (locale === 'ar' ? 'تواصل معنا' : 'Contact'))));
        setPageBody(String(pageRes.data?.body || ''));

        const pn = String((settingsRes.data as any)?.platform_name || 'ProFlow');
        setPlatformName(pn);
        setCompanyName(String((settingsRes.data as any)?.company_name || pn));
        setSupportEmail(String((settingsRes.data as any)?.support_email || ''));
        setSupportPhone(String((settingsRes.data as any)?.support_phone || ''));
        setAddress(String((settingsRes.data as any)?.company_address || ''));
        setWebsiteUrl(String((settingsRes.data as any)?.website_url || ''));
        setWhatsappNumber(String((settingsRes.data as any)?.whatsapp_number || ''));
      } catch (e: any) {
        setLoadError(e?.message || (t('publicWebsite:contact.loadError.default') || (locale === 'ar' ? 'فشل تحميل صفحة التواصل' : 'Failed to load contact page')));
      } finally {
        setLoading(false);
      }
    })();
  }, [t, locale]);

  const whatsappHref = useMemo(() => {
    const digits = String(whatsappNumber || '').replace(/\D/g, '');
    if (!digits) return '';
    return `https://wa.me/${digits}`;
  }, [whatsappNumber]);

  const safePageBody = useMemo(() => {
    const html = String(pageBody || '');
    if (!html.trim()) return '';
    return DOMPurify.sanitize(html, { FORBID_TAGS: ['script', 'style', 'iframe'] });
  }, [pageBody]);

  const canSubmit = useMemo(() => {
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    return name.length > 0 && email.length > 0 && message.length > 0;
  }, [form]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitError(null);
    setSubmitWarning(null);
    setSubmitted(false);

    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name) { setSubmitError(t('publicWebsite:contact.form.errors.nameRequired') || (locale === 'ar' ? 'الاسم مطلوب.' : 'Name is required.')); return; }
    if (!email || !email.includes('@')) { setSubmitError(t('publicWebsite:contact.form.errors.emailRequired') || (locale === 'ar' ? 'البريد الإلكتروني الصالح مطلوب.' : 'A valid email is required.')); return; }
    if (!message) { setSubmitError(t('publicWebsite:contact.form.errors.messageRequired') || (locale === 'ar' ? 'الرسالة مطلوبة.' : 'Message is required.')); return; }

    setSubmitting(true);
    try {
      const payload = {
        name,
        email,
        phone: form.phone.trim() || '',
        company_name: form.company_name.trim() || '',
        subject: form.subject.trim() || '',
        message,
        website: '',
        confirm_email: '',
        nickname: '',
      };
      const res = await fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(String((json as any)?.error || (t('publicWebsite:contact.form.errors.submitFail') || (locale === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to submit message'))));
      }
      setSubmitted(true);
      const warning = String((json as any)?.warning || '');
      if (warning) setSubmitWarning(warning);
      setForm({ name: '', email: '', phone: '', company_name: '', subject: '', message: '' });
    } catch (err: any) {
      setSubmitError(err?.message || (t('publicWebsite:contact.form.errors.submitFail') || (locale === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to submit message')));
    } finally {
      setSubmitting(false);
    }
  };

  const crumbsHome = t('publicWebsite:contact.crumbsHome') || (locale === 'ar' ? 'الرئيسية' : 'Home');
  const crumbsContact = t('publicWebsite:contact.crumbsContact') || (locale === 'ar' ? 'تواصل معنا' : 'Contact');

  return (
    <div className="min-h-screen bg-white">
      <PublicHero
        title={pageTitle}
        subtitle={(t('publicWebsite:contact.heroSubtitle') || (locale === 'ar' ? 'تواصل مع {company} لطرح الأسئلة حول المنتج، أو حجز العروض التوضيحية، أو دعم الفوترة، أو مساعدة في عملية الإعداد.' : 'Reach out to {company} for product questions, demos, billing support, or onboarding help.')).replace('{company}', companyName)}
        crumbs={[{ label: crumbsHome, href: '/' }, { label: crumbsContact }]}
      />
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-5">
            {loading && (
              <div className="card-base p-4 text-sm text-muted-foreground">
                {t('publicWebsite:contact.loadingDetails') || (locale === 'ar' ? 'جاري تحميل تفاصيل التواصل...' : 'Loading contact details...')}
              </div>
            )}
            {loadError && (
              <div className="card-base p-4 flex items-start gap-3 border border-danger/20">
                <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('publicWebsite:contact.loadError.title') || (locale === 'ar' ? 'فشل تحميل الصفحة' : 'Failed to load page')}</p>
                  <p className="text-xs text-muted-foreground">{loadError}</p>
                </div>
              </div>
            )}

            {safePageBody && (
              <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: safePageBody }} />
            )}

            <div className="card-base p-6 space-y-3">
              <h2 className="text-lg font-bold text-foreground">{t('publicWebsite:contact.support.title') || (locale === 'ar' ? 'الدعم' : 'Support')}</h2>
              <div className="flex flex-wrap gap-2">
                {supportEmail && (
                  <a href={`mailto:${supportEmail}`} className="btn-outline text-sm px-4 py-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t('publicWebsite:contact.support.email') || (locale === 'ar' ? 'الدعم عبر البريد' : 'Email Support')}
                  </a>
                )}
                {supportPhone && (
                  <a href={`tel:${supportPhone}`} className="btn-outline text-sm px-4 py-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {t('publicWebsite:contact.support.call') || (locale === 'ar' ? 'اتصال' : 'Call')}
                  </a>
                )}
                {whatsappHref && (
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm px-4 py-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    {t('publicWebsite:contact.support.whatsapp') || (locale === 'ar' ? 'واتساب' : 'WhatsApp')}
                  </a>
                )}
                {websiteUrl && (
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm px-4 py-2 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('publicWebsite:contact.support.website') || (locale === 'ar' ? 'الموقع الإلكتروني' : 'Website')}
                  </a>
                )}
              </div>
              {supportEmail && (
                <p className="text-sm text-muted-foreground">
                  {t('publicWebsite:contact.support.emailLabel') || (locale === 'ar' ? 'البريد الإلكتروني:' : 'Email:')} <a className="text-primary font-semibold hover:underline" href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </p>
              )}
              {supportPhone && (
                <p className="text-sm text-muted-foreground">
                  {t('publicWebsite:contact.support.phoneLabel') || (locale === 'ar' ? 'رقم الهاتف:' : 'Phone:')} <a className="text-primary font-semibold hover:underline" href={`tel:${supportPhone}`}>{supportPhone}</a>
                </p>
              )}
              {address && (
                <p className="text-sm text-muted-foreground">{t('publicWebsite:contact.support.addressLabel') || (locale === 'ar' ? 'العنوان:' : 'Address:')} {address}</p>
              )}
              {websiteUrl && (
                <p className="text-sm text-muted-foreground">
                  {t('publicWebsite:contact.support.websiteLabel') || (locale === 'ar' ? 'الموقع الإلكتروني:' : 'Website:')}{' '}
                  <a className="text-primary font-semibold hover:underline" href={websiteUrl} target="_blank" rel="noopener noreferrer">
                    {websiteUrl}
                  </a>
                </p>
              )}
            </div>
          </div>

          <div className="card-base p-6">
            <h2 className="text-lg font-bold text-foreground mb-1">{t('publicWebsite:contact.form.title') || (locale === 'ar' ? 'أرسل رسالة' : 'Send a message')}</h2>
            <p className="text-sm text-muted-foreground mb-5">
              {t('publicWebsite:contact.form.subtitle') || (locale === 'ar' ? 'أرسل رسالة وسوف يعود إليك فريقنا في أقرب وقت.' : 'Send a message and our team will get back to you.')}
            </p>
            {submitted && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{t('publicWebsite:contact.form.success') || (locale === 'ar' ? 'تم إرسال الرسالة بنجاح. وسوف نرد في أقرب وقت ممكن.' : 'Message sent successfully. We will respond as soon as possible.')}</p>
              </div>
            )}
            {submitWarning && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">{submitWarning}</p>
              </div>
            )}
            {submitError && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}
            <form className="space-y-3" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.name') || (locale === 'ar' ? 'الاسم الكامل' : 'Full Name')}</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={t('publicWebsite:contact.form.placeholders.name') || (locale === 'ar' ? 'اسمك' : 'Your name')}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.email') || (locale === 'ar' ? 'البريد الإلكتروني' : 'Email')}</label>
                  <input
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    type="email"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={t('publicWebsite:contact.form.placeholders.email') || (locale === 'ar' ? 'you@company.com' : 'you@company.com')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.phone') || (locale === 'ar' ? 'رقم الهاتف (اختياري)' : 'Phone (optional)')}</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={t('publicWebsite:contact.form.placeholders.phone') || (locale === 'ar' ? '+971 50 123 4567' : '+971 50 123 4567')}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.company') || (locale === 'ar' ? 'الشركة (اختياري)' : 'Company (optional)')}</label>
                  <input
                    value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={t('publicWebsite:contact.form.placeholders.company') || (locale === 'ar' ? 'اسم الشركة' : 'Company name')}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.subject') || (locale === 'ar' ? 'الموضوع' : 'Subject')}</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder={t('publicWebsite:contact.form.placeholders.subject') || (locale === 'ar' ? 'كيف يمكننا مساعدتك؟' : 'How can we help?')}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">{t('publicWebsite:contact.form.labels.message') || (locale === 'ar' ? 'الرسالة' : 'Message')}</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  placeholder={t('publicWebsite:contact.form.placeholders.message') || (locale === 'ar' ? 'أخبرنا ما الذي تحتاج إلى مساعدتك فيه...' : 'Tell us what you need help with...')}
                />
              </div>
              <button
                disabled={!canSubmit || submitting}
                className={`btn-primary w-full justify-center flex items-center gap-2 ${(!canSubmit || submitting) ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <MessageSquare className="w-4 h-4" />
                {submitting ? (t('publicWebsite:contact.form.sending') || (locale === 'ar' ? 'جاري الإرسال...' : 'Sending...')) : (t('publicWebsite:contact.form.submit') || (locale === 'ar' ? 'إرسال الرسالة' : 'Send Message'))}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                {t('publicWebsite:contact.form.agreement') || (locale === 'ar' ? 'من خلال الإرسال، أنت توافق على أننا قد نتواصل معك باستخدام التفاصيل المقدمة.' : 'By submitting, you agree we may contact you back using the details provided.')}
              </p>
            </form>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
